import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { UUID } from "crypto";
import { DocumentResponse } from "@interfaces/response";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { UploadCard } from "./UploadCard";

interface Props {
  title: string;
  /** Acepta nodos para los documentos cuyo detalle el diseño lista en viñetas. */
  sub?: ReactNode;
  required?: boolean;
  /** Código del catálogo de documentos (`DOCUMENTS_CATALOG` / `file_category_code`). */
  fileCode: string;
  /** Un elemento por slot; `undefined` = slot vacío sin archivo cargado. */
  documents: (DocumentResponse | undefined)[];
  onDocumentChange: (params: {
    newValue: DocumentResponse | undefined;
    valueAction: "add" | "update";
    valueIndex?: number;
  }) => void;
  disabled?: boolean;
  uploadType?: "client" | "person";
  clientDetailsId?: UUID;
  personId?: UUID;
  guaranteeId?: UUID;
  /** Número máximo de archivos permitidos; al alcanzarlo se bloquea el botón Agregar. */
  maxFiles?: number;
}

/**
 * Variante múltiple de `UploadCard`: muestra el encabezado (título, badge y subtítulo) una
 * sola vez y repite únicamente la zona del archivo (cuadro con nombre + acciones o dropzone)
 * por cada slot.
 *
 * La lógica de subida, eliminación y descarga vive en `UploadCard` con `hideHeader`; este
 * componente no la duplica.
 */
export const MultiUploadCard = ({
  title,
  sub,
  required,
  fileCode,
  documents,
  onDocumentChange,
  disabled,
  uploadType,
  clientDetailsId,
  personId,
  guaranteeId,
  maxFiles,
}: Props) => {
  // El badge del encabezado solo aparece mientras ningún slot tiene archivo cargado;
  // una vez que hay al menos uno, cada fila muestra su propio badge de estado.
  const todosPendientes = documents.every((d) => !d);
  const ultimoVacio = !documents.at(-1);
  const limiteAlcanzado = maxFiles !== undefined && documents.length >= maxFiles;

  return (
    <Box sx={{ bgcolor: "background.paper", py: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
        <Typography
          sx={{
            fontFamily: "Outfit",
            fontWeight: 600,
            fontSize: 13.5,
            color: "primary.dark",
            flex: 1,
            minWidth: 0,
            textAlign: "left",
          }}
        >
          {title}
          {required && (
            <Typography component="span" sx={{ color: "error.main", ml: "4px" }}>
              *
            </Typography>
          )}
        </Typography>
        {todosPendientes && <Badge tone="neutral">Pendiente</Badge>}
      </Box>

      {sub && (
        <Typography component="div" sx={{ fontSize: 11.5, color: "text.disabled", lineHeight: 1.5, textAlign: "left" }}>
          {sub}
        </Typography>
      )}

      <Box sx={{ mt: "2px" }}>
        {documents.map((doc, index) => (
          <Box key={index}>
            <UploadCard
              title={title}
              fileCode={fileCode}
              document={doc}
              hideHeader
              required={required}
              disabled={disabled}
              uploadType={uploadType}
              clientDetailsId={clientDetailsId}
              personId={personId}
              guaranteeId={guaranteeId}
              onDocumentChange={(newValue) => onDocumentChange({ newValue, valueAction: "update", valueIndex: index })}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box sx={limiteAlcanzado ? { cursor: "not-allowed" } : undefined}>
          <Button
            tone="ghost"
            startIcon={<Add sx={{ fontSize: 13 }} />}
            disabled={disabled || ultimoVacio || limiteAlcanzado}
            onClick={() => onDocumentChange({ newValue: undefined, valueAction: "add" })}
            data-testid={`multi-upload-${fileCode}-agregar-button`}
          >
            Agregar
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
